import React, { useState, useEffect } from 'react';
import { publicApi } from '@/services/api';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { resolveImage, SERVICE_IMAGE_BASE } from '@/lib/imageUrl';

const ServiciosV2 = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const response = await publicApi.getServices();
      setServices(response.data);
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-v2-darker min-h-screen">
      {/* Hero Banner */}
      <div className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://nayaceros.com/images/servicios/corte-con-plasma-cnc.jpg"
            alt="Servicios"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-v2-darker/90" />
        </div>
        <div className="relative max-w-7xl mx-auto px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="w-12 h-0.5 bg-v2-orange" />
            <span className="text-v2-orange uppercase tracking-wider text-sm font-medium">Lo que hacemos</span>
            <span className="w-12 h-0.5 bg-v2-orange" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Nuestros <span className="text-v2-orange">Servicios</span>
          </h1>
          <p className="text-xl text-v2-text-muted max-w-2xl mx-auto">
            Contamos con maquinaria especializada para la transformación de acero
          </p>
        </div>
      </div>

      {/* Services Grid */}
      <div className="max-w-7xl mx-auto px-8 py-20">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-v2-gray border border-v2-border animate-pulse">
                <div className="h-48 bg-v2-light-gray" />
                <div className="p-6">
                  <div className="h-6 bg-v2-light-gray rounded w-3/4 mb-3" />
                  <div className="h-4 bg-v2-light-gray rounded mb-2" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <div 
                key={service.id}
                className="group bg-v2-gray border border-v2-border hover:border-v2-orange overflow-hidden transition-all duration-300 hover:-translate-y-2"
                data-testid={`service-card-v2-${service.id}`}
              >
                <div className="relative overflow-hidden h-48">
                  <img 
                    src={resolveImage(service.ruta_img, SERVICE_IMAGE_BASE)}
                    alt={service.nombre}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => { e.target.src = 'https://placehold.co/400x300/1A1A1A/FF6B00?text=Servicio'; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-v2-darker via-transparent to-transparent" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-v2-orange transition-colors uppercase">
                    {service.nombre}
                  </h3>
                  <p className="text-v2-text-muted leading-relaxed text-sm">
                    {service.descripcion}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CTA */}
      <section className="py-20 bg-v2-dark border-y border-v2-border">
        <div className="max-w-7xl mx-auto px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            ¿Necesitas un servicio <span className="text-v2-orange">especial</span>?
          </h2>
          <p className="text-v2-text-muted text-lg max-w-2xl mx-auto mb-8">
            Ofrecemos maquila en general en acero al carbón, galvanizado, acero inoxidable y aluminio.
          </p>
          <Link 
            to="/contacto" 
            className="group inline-flex items-center gap-3 bg-v2-orange hover:bg-v2-orange-dark text-white font-bold uppercase tracking-wider px-10 py-5 transition-all duration-300"
            style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%)' }}
          >
            Contáctanos
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServiciosV2;
